import { Stack, Typography, useTheme } from "@mui/material";
import {
  DownArrowIcon,
  GlobeCheckmarkIcon,
  LockIcon,
  UserIcon,
} from "components";
import { Popover } from "components/popover";
import { memo, useMemo } from "react";
import type { AccessSpecifier } from "shared/types";

// ─── Types ───────────────────────────────────────────────────────────

interface AccessSpecifierMenuProps {
  activeMode: AccessSpecifier;
  onModeChange: (mode: AccessSpecifier) => void;
  isDisabled: boolean;
}

interface ModeOption {
  value: AccessSpecifier;
  label: string;
  icon: React.ReactElement;
  color: string | undefined;
}

// ─── Component ───────────────────────────────────────────────────────

export const AccessSpecifierMenu = memo(function AccessSpecifierMenu({
  activeMode,
  onModeChange,
  isDisabled,
}: AccessSpecifierMenuProps) {
  const theme = useTheme();

  const options: ModeOption[] = useMemo(
    () => [
      {
        value: "public",
        label: "Public",
        icon: <GlobeCheckmarkIcon sx={{ width: 16, height: 16 }} />,
        color: theme.palette.primitive.system.violet[800],
      },
      {
        value: "private",
        label: "Private",
        icon: <LockIcon sx={{ width: 16, height: 16 }} />,
        color: theme.palette.primitive.system.blue[800],
      },
      {
        value: "self",
        label: "Self",
        icon: <UserIcon sx={{ width: 16, height: 16 }} />,
        color: theme.palette.lightBg.high,
      },
    ],
    [theme],
  );

  const activeOption = options.find((o) => o.value === activeMode);

  return (
    <Popover
      isDisabled={isDisabled}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      paperSx={{ py: "4px", minWidth: "140px" }}
      popoverContent={(closePopover) => (
        <Stack>
          {options.map((option) => (
            <Stack
              key={option.value}
              direction="row"
              alignItems="center"
              gap="8px"
              onClick={() => {
                onModeChange(option.value);
                closePopover();
              }}
              sx={{
                px: "12px",
                py: "6px",
                cursor: "pointer",
                backgroundColor:
                  option.value === activeMode
                    ? theme.palette.neutral["010"]
                    : "transparent",
                "&:hover": {
                  backgroundColor: theme.palette.neutral["010"],
                },
              }}
            >
              <Stack sx={{ color: option.color }}>{option.icon}</Stack>
              <Typography
                variant="body-small"
                sx={{ color: theme.palette.lightBg.low }}
              >
                {option.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap="2px"
        sx={{
          cursor: isDisabled ? "default" : "pointer",
          flexShrink: 0,
          userSelect: "none",
        }}
      >
        <Stack sx={{ color: activeOption?.color }}>{activeOption?.icon}</Stack>

        <DownArrowIcon
          sx={{
            width: 14,
            height: 14,
            color: activeOption?.color,
          }}
        />
      </Stack>
    </Popover>
  );
});
